import type { RitualCardProps } from "@/components/cards/ritualCard"
import { RitualCard } from "@/components/cards/ritualCard"
import { formatDuration } from "@/features/rituals/utils/formatDuration"
import { type IntentionRitual, RITUAL_FORMAT_LABELS } from "../types"

function mapRitualToCardProps(
  ritual: IntentionRitual,
  onBookmark?: (id: string) => void,
): RitualCardProps {
  const user = ritual.user
  const username = user?.name ?? user?.username ?? "Unknown"

  const tags: string[] = [
    ritual.isPaid ? "Paid" : "Free",
    RITUAL_FORMAT_LABELS[ritual.ritualType] ?? ritual.ritualType,
  ]

  return {
    id: ritual.id,
    image: ritual.coverImage ?? "",
    userAvatar: user?.profilePictureUrl ?? undefined,
    username,
    userHandle: "",
    title: ritual.title,
    tags,
    duration: formatDuration(ritual.duration, ritual.durationType),
    isBookmarked: ritual.isSaved,
    onBookmarkClick: onBookmark ? () => onBookmark(ritual.id) : undefined,
    userId: user?.id,
    participationFee: ritual.isPaid ? "Paid" : "Free",
    price: ritual.fee ?? undefined,
    ritualTs: ritual.ritualTs,
    city: ritual.city,
    state: ritual.state,
  }
}

interface IntentionRitualGridProps {
  rituals: IntentionRitual[]
  isLoading: boolean
  hasMore: boolean
  onLoadMore: () => void
  onBookmark?: (ritualId: string) => void
}

export function IntentionRitualGrid({
  rituals,
  isLoading,
  hasMore,
  onLoadMore,
  onBookmark,
}: IntentionRitualGridProps) {
  if (!isLoading && rituals.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-white/60 text-lg">No rituals found for this intention.</p>
        <p className="text-white/40 text-sm mt-2">Try adjusting your filters or search query.</p>
      </div>
    )
  }

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
        {rituals.map(ritual => (
          <div key={ritual.id}>
            <RitualCard {...mapRitualToCardProps(ritual, onBookmark)} />
          </div>
        ))}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
          <div
            className="h-[440px] bg-white/5 rounded-tl-[40px] rounded-br-[40px] animate-pulse"
            aria-hidden="true"
          />
          <div
            className="h-[440px] bg-white/5 rounded-tl-[40px] rounded-br-[40px] animate-pulse"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Load More button */}
      {hasMore && !isLoading && (
        <div className="flex justify-center mt-10 mb-[90px]">
          <button
            type="button"
            onClick={onLoadMore}
            className="h-[50px] w-[280px] rounded-full border border-primary text-primary text-sm font-semibold uppercase hover:bg-primary/5 transition-colors"
          >
            Load more
          </button>
        </div>
      )}
    </div>
  )
}
